import { variation } from "../services/launchDarkly";
import { getStore } from "../store/createStore";
import userSelectors from "../store/user/selectors";
import { getCookie } from "./cookies";
import { safeParse, IsStorageSupported } from "./helpers";
import { isAuthenticated } from "./currentUser";
import { captureError } from "./error";
import { withCMP } from "./segment";

// FullStory configuration
export const FULLSTORY_INTERACTION_DELAY_MS = 1000; // 1 second after interaction
const VERBOSE_FULLSTORY_LOGGING =
  process.env.NODE_ENV === "development" ||
  (typeof window !== "undefined" &&
    window.location.search.includes("fullstory-debug"));

let fallbackAnonymousId = "";
let fullStoryLoaderInitialized = false;
let analyticsSetupInitialized = false;

// Helper function for verbose FullStory logging
const logFullStory = (message, isWarn = false) => {
  if (VERBOSE_FULLSTORY_LOGGING) {
    if (isWarn) {
      // eslint-disable-next-line no-console
      console.warn(message);
    } else {
      // eslint-disable-next-line no-console
      console.log(message);
    }
  }
};

const FallbackUser = {
  id: () => "",
  anonymousId: () => fallbackAnonymousId,
  traits: () => {
    return {};
  },
};

export const analytics = {
  on: (...args) =>
    callIfPresent(window.analytics && window.analytics.on, { args }),

  /**
   * Waits for window.analytics to be available and window.analytics.ready to
   * be called before returning the Segment user info. If window.analytics is
   * not available when this function is called, we continuously poll until
   * it becomes available.
   *
   * @return {Object} the userInfo object { id:, anonymousId:, traits: }
   */
  waitForUserInfo() {
    return waitForAnalyticsAndGetUser();
  },

  /**
   * Synchronously gets the user info from stored Segment data without waiting
   * for window.analytics or window.analytics.ready. Most of the time (unless
   * Cookies are cleared or it's a first time load), the stored Segment data
   * will be accurate. If the stored data is not available, we return a user
   * with a mock anonymousId.
   */
  getStoredUserInfo() {
    return getUserInfo();
  },

  setAnonymousId: (anonymousId) => {
    fallbackAnonymousId = anonymousId;
    callIfPresent(window.analytics && window.analytics.setAnonymousId, {
      args: [anonymousId],
    });
  },

  get anonymousId() {
    return getAnonymousId();
  },
};

export const fixFullstoryIntegration = ({ payload, next }) => {
  if (
    payload.obj.integrations &&
    payload.obj.integrations.hasOwnProperty("Fullstory (Actions)")
  ) {
    payload.obj.integrations.FullStory =
      payload.obj.integrations["Fullstory (Actions)"];
    delete payload.obj.integrations["Fullstory (Actions)"];
  }
  next(payload);
};

export const pushUserTraitsToDataLayer = ({ payload, next }) => {
  if (
    payload.obj.type === "identify" &&
    typeof window !== "undefined" &&
    typeof window.dataLayer !== "undefined" &&
    window.analytics
  ) {
    window.dataLayer.push(window.analytics.user().traits());
  }
  next(payload);
};

export const injectIPv6Context = ({ payload, next }) => {
  let ipv6 =
    typeof window !== "undefined" ? sessionStorage.getItem("clientIPv6") : null;

  if (ipv6) {
    payload.obj.context = {
      ...payload.obj.context,
      ipv6,
    };
  }
  next(payload);
};

export const injectIntercomJwt = ({ payload, next }) => {
  // Inject the Intercom JWT for identify and page events
  if (!["identify", "page"].includes(payload.obj.type)) {
    return next(payload);
  }

  const state = getStore().getState();
  const user = userSelectors.activeUser(state);
  const payloadUserId = payload.obj.userId;

  // Disable Intercom if we can't provide a valid JWT.
  // This prevents "User ID mismatch" errors when:
  // 1. No user is loaded in Redux state
  // 2. User exists but intercomJwt hasn't been fetched yet
  // 3. The payload userId doesn't match the Redux user (e.g., stale localStorage)
  if (
    !user ||
    !user.intercomJwt ||
    (payloadUserId && user.id !== payloadUserId)
  ) {
    payload.obj.integrations = {
      ...payload.obj.integrations,
      Intercom: false,
    };
    return next(payload);
  }

  payload.obj.integrations = {
    ...payload.obj.integrations,
    Intercom: {
      ...(payload.obj.integrations?.Intercom || {}),
      user_jwt: user.intercomJwt,
    },
  };
  next(payload);
};

export const injectFacebookCookies = ({ payload, next }) => {
  if (!["track", "page"].includes(payload.obj.type)) return next(payload);

  const fbp = getCookie("_fbp");
  const fbc = getCookie("_fbc");

  if (!fbp && !fbc) return next(payload);

  const fbIntegrations = {
    ...(fbp && { fbp }),
    ...(fbc && { fbc }),
  };

  // TODO: Deprecate adding to properties in favor of integrations only in the future
  payload.obj.properties = {
    ...payload.obj.properties,
    ...fbIntegrations,
  };

  payload.obj.integrations = {
    ...payload.obj.integrations,
    "Facebook Conversions API (Actions)": {
      ...(payload.obj.integrations?.["Facebook Conversions API (Actions)"] ||
        {}),
      ...fbIntegrations,
    },
  };

  next(payload);
};

export const injectGoogleCookies = ({ payload, next }) => {
  if (!["track", "page"].includes(payload.obj.type)) return next(payload);

  const _gcl_aw = getCookie("_gcl_aw");
  const gclid = getCookie("gclid");

  if (_gcl_aw || gclid) {
    payload.obj.integrations = {
      ...payload.obj.integrations,
      "Google Enhanced Conversions": {
        ...(payload.obj.integrations?.["Google Enhanced Conversions"] || {}),
        ...(_gcl_aw && { _gcl_aw }),
        ...(gclid && { gclid }),
      },
    };
  }

  next(payload);
};

export const injectTikTokCookies = ({ payload, next }) => {
  if (!["track", "page"].includes(payload.obj.type)) return next(payload);

  const ttp = getCookie("_ttp");
  const ttclid = getCookie("ttclid");

  if (ttp || ttclid) {
    payload.obj.integrations = {
      ...payload.obj.integrations,
      "Tiktok Conversions": {
        ...(payload.obj.integrations?.["Tiktok Conversions"] || {}),
        ...(ttp && { ttp }),
        ...(ttclid && { ttclid }),
      },
    };
  }

  next(payload);
};

export const injectIterableCookies = ({ payload, next }) => {
  if (!["track", "page"].includes(payload.obj.type)) return next(payload);

  const iterableCampaignId = getCookie("iterableEmailCampaignId");
  const iterableTemplateId = getCookie("iterableTemplateId");
  const messageId = getCookie("iterableMessageId");

  if (iterableCampaignId || iterableTemplateId || messageId) {
    const campaignId = Number(iterableCampaignId);
    const templateId = Number(iterableTemplateId);

    payload.obj.integrations = {
      ...payload.obj.integrations,
      "Iterable (Actions)": {
        ...(payload.obj.integrations?.["Iterable (Actions)"] || {}),
        ...(campaignId && !isNaN(campaignId) && { campaignId }),
        ...(templateId && !isNaN(templateId) && { templateId }),
        ...(messageId && { messageId }),
      },
    };
  }

  next(payload);
};

export const injectRedditCookies = ({ payload, next }) => {
  if (!["track", "page"].includes(payload.obj.type)) return next(payload);

  const _rdt_uuid = getCookie("_rdt_uuid");

  if (_rdt_uuid) {
    payload.obj.integrations = {
      ...payload.obj.integrations,
      "Reddit Conversions Api": {
        ...(payload.obj.integrations?.["Reddit Conversions Api"] || {}),
        ...(_rdt_uuid && { _rdt_uuid }),
      },
    };
  }

  next(payload);
};

export const injectPinterestCookies = ({ payload, next }) => {
  if (!["track", "page"].includes(payload.obj.type)) return next(payload);

  const epik = getCookie("_epik");

  if (epik) {
    payload.obj.integrations = {
      ...payload.obj.integrations,
      "Pinterest Conversions API": {
        ...(payload.obj.integrations?.["Pinterest Conversions API"] || {}),
        ...(epik && { epik }),
      },
    };
  }

  next(payload);
};

/**
 * Injects authentication status into Segment events to help understand user behavior
 * and optimize experiences for authenticated vs unauthenticated users.
 *
 * This middleware adds an `is_authenticated` property to:
 * - Track events: added to properties
 * - Page events: added to properties
 * - Identify events: added to traits
 *
 * This helps answer questions like:
 * - What percentage of users visit the site unauthenticated but have identifiers?
 * - How often do unauthenticated users make purchases with existing account emails?
 * - Which features are used more by authenticated vs unauthenticated users?
 */
export const injectAuthenticationStatus = ({ payload, next }) => {
  // Only inject authentication status for track, identify, and page events
  if (!["track", "identify", "page"].includes(payload.obj.type)) {
    return next(payload);
  }

  let authenticated = false;

  try {
    authenticated = isAuthenticated();
  } catch (error) {
    captureError(
      error,
      "Failed to determine authentication status for analytics middleware",
    );
    // Default to false for safety when authentication check fails
    authenticated = false;
  }

  // Add is_authenticated to properties for track and page events
  if (payload.obj.type === "track" || payload.obj.type === "page") {
    payload.obj.properties = {
      ...payload.obj.properties,
      is_authenticated: authenticated,
    };
  }

  // Add is_authenticated to traits for identify events
  if (payload.obj.type === "identify") {
    payload.obj.traits = {
      ...payload.obj.traits,
      is_authenticated: authenticated,
    };
  }

  next(payload);
};

/**
 * Sets up event listeners for conditional FullStory loading
 * FullStory will start 1 second after user interacts with the page
 * (click, scroll, touch, or key press)
 */
export const setupConditionalFullStoryLoader = () => {
  // Prevent multiple executions to avoid memory leaks from duplicate listeners/timers
  if (fullStoryLoaderInitialized) {
    logFullStory("🔄 [FullStory] Loader already initialized, skipping");
    return;
  }
  fullStoryLoaderInitialized = true;
  logFullStory("🚀 [FullStory] Initializing conditional loader");

  let fullStoryStarted = false;
  let interactionTimeoutId = null;

  const startFullStory = (trigger = "unknown") => {
    if (fullStoryStarted) return;
    fullStoryStarted = true;
    logFullStory(
      `⚡ [FullStory] Starting FullStory (triggered by: ${trigger})`,
    );

    // Clear any pending interaction timeout
    if (interactionTimeoutId) {
      logFullStory("⏰ [FullStory] Clearing interaction timeout timer");
      clearTimeout(interactionTimeoutId);
      interactionTimeoutId = null;
    }

    // Remove all event listeners
    logFullStory("🧹 [FullStory] Removing event listeners");
    document.removeEventListener("click", clickHandler, {
      capture: true,
      passive: true,
    });
    document.removeEventListener("scroll", scrollHandler, { passive: true });
    document.removeEventListener("touchstart", touchHandler, {
      passive: true,
    });
    document.removeEventListener("keydown", keyHandler, { passive: true });

    // Start FullStory
    const fsAvailable = window && window.FS;
    if (fsAvailable) {
      logFullStory("✅ [FullStory] window.FS available, calling start()");
      window.FS("start");
    } else {
      logFullStory("⚠️  [FullStory] window.FS not available", true);
    }
  };

  const scheduleFullStoryStart = (interactionType) => {
    // If already scheduled or started, do nothing
    if (interactionTimeoutId || fullStoryStarted) return;

    logFullStory(
      `📅 [FullStory] Scheduling start in ${FULLSTORY_INTERACTION_DELAY_MS}ms after ${interactionType} interaction`,
    );
    interactionTimeoutId = setTimeout(() => {
      startFullStory(`${interactionType}+delay`);
    }, FULLSTORY_INTERACTION_DELAY_MS);
  };

  // Create named handler functions for better debugging
  const clickHandler = () => scheduleFullStoryStart("click");
  const scrollHandler = () => scheduleFullStoryStart("scroll");
  const touchHandler = () => scheduleFullStoryStart("touchstart");
  const keyHandler = () => scheduleFullStoryStart("keydown");

  // Set up interaction listeners (no initial timeout)
  logFullStory(
    "👂 [FullStory] Setting up interaction listeners (no auto-timeout)",
  );
  document.addEventListener("click", clickHandler, {
    capture: true,
    passive: true,
  });
  document.addEventListener("scroll", scrollHandler, { passive: true });
  document.addEventListener("touchstart", touchHandler, { passive: true });
  document.addEventListener("keydown", keyHandler, { passive: true });
  logFullStory("✅ [FullStory] Conditional loader setup complete");
};

/**
 * Handles FullStory sampling and initialization
 * Now uses conditional loading based on feature flag and user interaction/timeout
 */
export const setupFullStory = () => {
  logFullStory(
    "🎯 [FullStory] Checking feature flag: fullstory-session-capture",
  );
  const fullstorySampled = variation("fullstory-session-capture", false);
  logFullStory(`🎲 [FullStory] Feature flag result: ${fullstorySampled}`);

  if (fullstorySampled) {
    logFullStory(
      "🟢 [FullStory] Feature flag enabled, setting up conditional loader",
    );
    setupConditionalFullStoryLoader();
  } else {
    logFullStory(
      "🔴 [FullStory] Feature flag disabled, FullStory will not load",
    );
  }
};

/**
 * Registers analytics middleware and resolves when analytics is ready
 * @returns {Promise<void>}
 */
const setupAnalytics = async () => {
  await new Promise((resolve) => {
    // Only register middleware once to prevent duplicates
    if (!analyticsSetupInitialized) {
      analyticsSetupInitialized = true;
      window.analytics.addSourceMiddleware(fixFullstoryIntegration);
      window.analytics.addSourceMiddleware(pushUserTraitsToDataLayer);
      window.analytics.addSourceMiddleware(injectIPv6Context);
      window.analytics.addSourceMiddleware(injectIntercomJwt);
      window.analytics.addSourceMiddleware(injectFacebookCookies);
      window.analytics.addSourceMiddleware(injectGoogleCookies);
      window.analytics.addSourceMiddleware(injectTikTokCookies);
      window.analytics.addSourceMiddleware(injectIterableCookies);
      window.analytics.addSourceMiddleware(injectRedditCookies);
      window.analytics.addSourceMiddleware(injectPinterestCookies);
      window.analytics.addSourceMiddleware(injectAuthenticationStatus);

      withCMP(window.analytics).load(process.env.GATSBY_SEGMENT_WRITE_KEY, {
        integrations: {
          "Segment.io": {
            deliveryStrategy: {
              config: {
                keepalive: true,
              },
            },
          },
        },
      });
    }
    window.analytics.ready(() => {
      logFullStory("📊 [Analytics] Analytics ready, initializing FullStory");
      setupFullStory();
      resolve();
    });
  });
};

export const registerMiddleware = async () => {
  if (window.analytics && typeof window.analytics.ready === "function") {
    await setupAnalytics();
    return getUserInfo();
  }

  await new Promise((resolve) => setTimeout(resolve, 100));
  return waitForAnalyticsAndGetUser();
};

/**
 * Polls for windows.analytics and window.analytics.ready and resolves with
 * the Segment userInfo. Waiting for the ready function ensures that the
 * window.analytics.user function will be available, and that the userInfo
 * returned will be up to date.
 *
 * If window.analytics or window.analytics.ready never become available, then
 * this function will never resolve.
 *
 * @return {Promise<Object>} a promise that resolves to the userInfo
 */
const waitForAnalyticsAndGetUser = async () => {
  if (window.analytics && typeof window.analytics.ready === "function") {
    await setupAnalytics();
    return getUserInfo();
  }

  await new Promise((resolve) => setTimeout(resolve, 100));
  return waitForAnalyticsAndGetUser();
};

if (typeof window !== "undefined") {
  registerMiddleware();
}

function getUserInfo() {
  return {
    id: getUserId(),
    anonymousId: getAnonymousId(),
    traits: getUserTraits(),
  };
}

function getUser() {
  return callIfPresent(window.analytics && window.analytics.user, {
    defaultReturn: FallbackUser,
  });
}

/**
 * Retrieves Segment storage data from Cookies based on the specified
 * cookieKey. If the value is not available, the onNoValue function called
 * instead.
 *
 * @param {String} cookieKey
 * @param {Function} onNoValue the function to call if the value is not
 *  available in storage
 * @return {*} The parsed JSON value
 */
export function getCookieValue(cookieKey, onNoValue) {
  const cookieValue = getCookie(cookieKey);
  return cookieValue ? safeParse(cookieValue, cookieValue) : onNoValue();
}

/**
 * Retrieves Segment storage data from Local Storage based on the specified
 * storageKey. If the value is not available, the onNoValue function called
 * instead.
 *
 * @param {String} storageKey
 * @param {Function} onNoValue the function to call if the value is not
 *  available in storage
 * @return {*} The parsed JSON value
 */
export function getStorageValue(storageKey, onNoValue) {
  if (!IsStorageSupported) return onNoValue();

  const storeValue = window.localStorage.getItem(storageKey);
  return storeValue ? safeParse(storeValue, storeValue) : onNoValue();
}

function getUserId() {
  return getCookieValue("ajs_user_id", getUser().id);
}

function getAnonymousId() {
  return getCookieValue("ajs_anonymous_id", getUser().anonymousId);
}

function getUserTraits() {
  // The ajs_user_traits are only persisted in Local Storage.
  return getStorageValue("ajs_user_traits", getUser().traits);
}

/** Non-analytics related helper functions */

function callIfPresent(fn, options = {}) {
  const { args = [], defaultReturn } = options;
  const callee =
    typeof fn === "function"
      ? fn
      : () => {
          return defaultReturn;
        };
  return callee(...args);
}
